import { Card, Col } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import comingSoonEvent from "../../assets/lottie-files/comingSoonEvent.lottie";
import eventPic from "../../assets/markets/Tombolo.jpg";
import { useState } from "react";
import EventModal from "./EventModal";
import { sanityImageUrl } from "../../sanityClient";

const TAG_COLORS: Record<string, { bg: string; color: string }> = {
  Multicultural: { bg: "#fff3cd", color: "#7a5c00" },
  Food: { bg: "#d4edda", color: "#1a5c32" },
  Music: { bg: "#cce5ff", color: "#004085" },
  Crafts: { bg: "#fde8e8", color: "#8b2020" },
  Workshop: { bg: "#ede0f8", color: "#5a1e8a" },
  Holiday: { bg: "#ffd6e0", color: "#8b0000" },
  Vintage: { bg: "#f5e6d0", color: "#7a4a00" },
  Art: { bg: "#f5d0f0", color: "#7a0070" },
  Beer: { bg: "#fff3d0", color: "#8a5c00" },
  Books: { bg: "#d0e8ff", color: "#003580" },
  Literature: { bg: "#d0e8ff", color: "#003580" },
  Fitness: { bg: "#d0ffd8", color: "#005c10" },
  Yoga: { bg: "#d0ffd8", color: "#005c10" },
  Animals: { bg: "#ffe8d0", color: "#7a3000" },
  "LGBTQ+": { bg: "#ffd0ee", color: "#7a0054" },
  Cultural: { bg: "#fff3cd", color: "#7a5c00" },
  Shopping: { bg: "#d0f0ff", color: "#005080" },
  Handmade: { bg: "#fde8e8", color: "#8b2020" },
  "Family-Friendly": { bg: "#d0ffd8", color: "#005c10" },
  Chocolate: { bg: "#f0e0d0", color: "#5a2500" },
  Cocktail: { bg: "#ffe8d0", color: "#7a3000" },
  Mocktail: { bg: "#ffe8d0", color: "#7a3000" },
  DIY: { bg: "#e0f0ff", color: "#003580" },
  Tools: { bg: "#e0f0ff", color: "#003580" },
  Easter: { bg: "#f0e8ff", color: "#5a008a" },
  "Beginner-Friendly": { bg: "#d0ffd8", color: "#005c10" },
  Ramadan: { bg: "#d0f0e8", color: "#005040" },
  Evening: { bg: "#e8d0f8", color: "#3d0080" },
  Prints: { bg: "#f5d0f0", color: "#7a0070" },
  Dance: { bg: "#cce5ff", color: "#004085" },
  Gifts: { bg: "#ffd6e0", color: "#8b0000" },
  Collectibles: { bg: "#f5e6d0", color: "#7a4a00" },
};

type EventProps = {
  event: {
    id: string;
    title: string;
    description: string;
    dates: string[];
    displayDate: string;
    time: string;
    image?: { asset: { _ref: string } };
    imageUrl?: string;
    admission?: string;
    highlights?: string[];
    tags?: string[];
    tickets?: string;
    paymentLink?: string;
    organizer?: {
      name?: string;
      email?: string;
      phone?: string;
      website?: string;
    };
  };
};

export default function EventCard({ event }: EventProps) {
  const [showModal, setShowModal] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isPast = event.dates.every((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day).getTime() < today.getTime();
  });

  const images = (require as any).context("../../assets", true);
  const resolveImageSrc = (path: string | undefined): string | undefined => {
    if (!path || path.length === 0) return undefined;
    const trimmed = path.trim();
    if (
      trimmed.startsWith("http://") ||
      trimmed.startsWith("https://") ||
      trimmed.startsWith("/")
    ) {
      return trimmed;
    }
    try {
      const relative = trimmed.replace(/^.*\/assets\//, "./");
      return images(relative).default ?? images(relative);
    } catch {
      return undefined;
    }
  };

  const imgSrc = event.image?.asset
    ? sanityImageUrl(event.image)
    : resolveImageSrc(event.imageUrl);

  return (
    <Col xs={12} sm={6} md={6} lg={4} xl={4} className="mb-4">
      <Card
        className="h-100"
        style={{
          backgroundColor: "#fff",
          boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
          borderRadius: "8px",
          border: "1px solid #D9D9D9",
          maxWidth: "350px",
          margin: "0 auto",
        }}
      >
        <Card.Body
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <div>
            <div
              style={{
                position: "relative",
                width: "100%",
                height: "200px",
                marginBottom: "12px",
                overflow: "hidden",
                borderRadius: "5px",
                backgroundColor: "#f3f3f3",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {!imgSrc ? (
                <DotLottieReact
                  src={comingSoonEvent}
                  loop
                  autoplay
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <img
                  style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "top" }}
                  src={imgSrc}
                  alt={event.title}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      eventPic as unknown as string;
                  }}
                />
              )}
              {isPast && (
                <>
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      background: "rgba(0, 0, 0, 0.45)",
                      zIndex: 1,
                      borderRadius: "5px",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "12px",
                      left: "0",
                      background: "linear-gradient(135deg, #dc3545, #b02a37)",
                      color: "#fff",
                      padding: "6px 16px",
                      fontSize: "0.85rem",
                      fontWeight: "800",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      borderRadius: "0 6px 6px 0",
                      boxShadow: "0 3px 8px rgba(0,0,0,0.3)",
                      zIndex: 2,
                    }}
                  >
                    Past Event
                  </div>
                </>
              )}
            </div>

            <div style={{ textAlign: "left", marginBottom: "10px" }}>
              <Card.Title style={{ fontWeight: "bold", marginBottom: "6px" }}>
                {event.title}
              </Card.Title>

              {event.tags && event.tags.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "5px",
                    marginBottom: "10px",
                  }}
                >
                  {event.tags.slice(0, 3).map((tag) => {
                    const c = TAG_COLORS[tag] ?? { bg: "#e8e8e8", color: "#444" };
                    return (
                      <span
                        key={tag}
                        style={{
                          padding: "2px 9px",
                          borderRadius: "20px",
                          fontSize: "0.72rem",
                          fontWeight: 600,
                          backgroundColor: c.bg,
                          color: c.color,
                        }}
                      >
                        {tag}
                      </span>
                    );
                  })}
                </div>
              )}

              <Card.Text as="div">
                <div style={{ color: "#555", fontSize: "0.9rem" }}>
                  {event.displayDate}{" "}
                  {event.dates.length > 1 && (
                    <span
                      style={{
                        marginLeft: "6px",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontSize: "0.72rem",
                        fontWeight: "600",
                        color: "#fff",
                        background: "linear-gradient(90deg, #FF7E5F, #FF6A00)",
                        verticalAlign: "middle",
                        display: "inline-block",
                        lineHeight: "1.5",
                      }}
                    >
                      Multiple Dates
                    </span>
                  )}
                </div>
                <div style={{ color: "#555", fontSize: "0.9rem" }}>{event.time}</div>
                {event.admission && (
                  <div
                    style={{
                      marginTop: "4px",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      color: event.admission === "Free" ? "#1a5c32" : "#D46540",
                    }}
                  >
                    {event.admission === "Free" ? "Free Admission" : event.admission}
                  </div>
                )}
              </Card.Text>
            </div>
          </div>

          <div style={{ marginTop: "auto", textAlign: "left" }}>
            <Button
              onClick={() => setShowModal(true)}
              variant="dark"
              style={{
                backgroundColor: "#2C2C2C",
                fontFamily: "Yanone Kaffeesatz",
                fontWeight: "bold",
                width: "100%",
              }}
            >
              Learn more
            </Button>
          </div>
        </Card.Body>
      </Card>

      <EventModal
        show={showModal}
        onHide={() => setShowModal(false)}
        event={event}
      />
    </Col>
  );
}
